import { Drawer } from "vaul";
import { Button } from "@/components/ui/button";
import { usePWA } from "@/hooks/usePWA";
import { Share, PlusSquare, Download } from "lucide-react";
import { useState, useEffect } from "react";

export const PWAInstallDrawer = () => {
    const { isInstallable, isPWA, isIOS, installPWA } = usePWA();
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        // Show drawer if installable or if on iOS and not in PWA mode (and haven't dismissed it recently)
        if (!isPWA) {
            const hasDismissed = sessionStorage.getItem('pwa-drawer-dismissed');
            if (!hasDismissed) {
                // Delay showing to not be intrusive immediately
                const timer = setTimeout(() => {
                    if (isInstallable || isIOS) {
                        setIsOpen(true);
                    }
                }, 3000);
                return () => clearTimeout(timer);
            }
        }
    }, [isInstallable, isPWA, isIOS]);

    const handleDismiss = () => {
        setIsOpen(false);
        sessionStorage.setItem('pwa-drawer-dismissed', 'true');
    };

    if (isPWA) return null;
    if (!isInstallable && !isIOS) return null;

    return (
        <Drawer.Root open={isOpen} onOpenChange={setIsOpen}>
            <Drawer.Portal>
                <Drawer.Overlay className="fixed inset-0 bg-black/40 z-50" onClick={handleDismiss} />
                <Drawer.Content className="bg-background flex flex-col rounded-t-[10px] mt-24 fixed bottom-0 left-0 right-0 z-50 max-h-[96%] outline-none border-t border-border">
                    <div className="p-4 bg-background rounded-t-[10px] flex-1">
                        <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-muted mb-8" />

                        <div className="max-w-md mx-auto">
                            <div className="flex flex-col items-center text-center mb-6">
                                <img
                                    src="/lovable-uploads/f60fddfc-2876-455b-baed-f173808a0bbe.png"
                                    alt="VasisPro Logo"
                                    className="w-16 h-16 rounded-xl shadow-lg mb-4"
                                />
                                <Drawer.Title className="font-bold text-xl mb-2">
                                    Install VasisPro
                                </Drawer.Title>
                                <p className="text-muted-foreground text-sm">
                                    Install the app for a better experience with offline access, smoother performance, and quick access from your home screen.
                                </p>
                            </div>

                            {isIOS ? (
                                <div className="space-y-4 bg-muted/30 p-4 rounded-lg border border-border/50">
                                    <div className="flex items-center gap-3 text-sm">
                                        <div className="bg-background p-2 rounded-md shadow-sm border border-border">
                                            <Share className="w-5 h-5 text-primary" />
                                        </div>
                                        <span>Tap the <strong>Share</strong> button in your browser menu</span>
                                    </div>
                                    <div className="w-px h-4 bg-border ml-6" />
                                    <div className="flex items-center gap-3 text-sm">
                                        <div className="bg-background p-2 rounded-md shadow-sm border border-border">
                                            <PlusSquare className="w-5 h-5 text-primary" />
                                        </div>
                                        <span>Select <strong>Add to Home Screen</strong></span>
                                    </div>
                                </div>
                            ) : (
                                <Button
                                    className="w-full py-6 text-lg font-medium shadow-lg active:scale-[0.98] transition-transform"
                                    onClick={installPWA}
                                >
                                    <Download className="mr-2 w-5 h-5" />
                                    Install App
                                </Button>
                            )}

                            <div className="mt-6 text-center">
                                <button
                                    onClick={handleDismiss}
                                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    Maybe later
                                </button>
                            </div>
                        </div>
                    </div>
                </Drawer.Content>
            </Drawer.Portal>
        </Drawer.Root>
    );
};
